import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  AppSkeleton,
  CardSkeleton,
  FormSkeleton,
  ListPageSkeleton,
  TableSkeleton,
} from '@/modules/shared/components/ui/AppSkeleton';

const LOADER_BLOCK = 'bg-muted rounded-md shadow-sm animate-pulse skeleton-shimmer motion-reduce:animate-none';

export interface AppLoaderProps {
  className?: string;
}

export function AppLoader({ className }: AppLoaderProps) {
  return <AppPageSkeleton className={className} />;
}

export interface AppPageSkeletonProps {
  className?: string;
  showFilters?: boolean;
  showSearch?: boolean;
  columns?: number;
  rows?: number;
}

export function AppPageSkeleton({
  className,
  showFilters = true,
  showSearch = true,
  columns = 5,
  rows = 8,
}: AppPageSkeletonProps) {
  return (
    <div aria-busy="true" aria-live="polite" role="status" className={className}>
      <span className="sr-only">Loading content</span>
      <ListPageSkeleton
        className="w-full"
        showFilters={showFilters}
        showSearch={showSearch}
        columns={columns}
        rows={rows}
      />
    </div>
  );
}

export interface AppCardSkeletonProps {
  className?: string;
  count?: number;
  lines?: number;
}

export function AppCardSkeleton({ className, count = 3, lines }: AppCardSkeletonProps) {
  const resolvedCount = lines ?? count;
  return (
    <div aria-busy="true" role="status" className={className}>
      <span className="sr-only">Loading content</span>
      <CardSkeleton count={resolvedCount} />
    </div>
  );
}

export interface AppTableSkeletonProps {
  className?: string;
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  columnWidths?: string[];
}

export function AppTableSkeleton({
  className,
  rows = 10,
  columns = 5,
  showHeader = true,
  columnWidths,
}: AppTableSkeletonProps) {
  if (!showHeader && !columnWidths) {
    return (
      <div aria-busy="true" role="status" className={className}>
        <span className="sr-only">Loading content</span>
        <TableSkeleton rows={rows} columns={columns} />
      </div>
    );
  }

  const gridTemplateColumns = columnWidths && columnWidths.length === columns
    ? columnWidths.join(' ')
    : `repeat(${columns}, minmax(0, 1fr))`;

  return (
    <div
      aria-busy="true"
      role="status"
      className={cn('w-full overflow-hidden rounded-xl border border-border-hairline bg-surface-premium', className)}
    >
      <span className="sr-only">Loading content</span>
      {showHeader ? (
        <div
          className="grid items-center gap-4 px-4 py-3 bg-surface-premium-muted border-b border-border-hairline"
          style={{ gridTemplateColumns }}
        >
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className={cn(LOADER_BLOCK, 'h-3', i === 0 ? 'w-3/4' : 'w-2/3')} aria-hidden="true" />
          ))}
        </div>
      ) : null}

      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="grid items-center gap-4 px-4 py-3 border-b border-border last:border-b-0"
          style={{ gridTemplateColumns }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              key={colIndex}
              className={cn(LOADER_BLOCK, 'h-3', colIndex === 0 ? 'w-4/5' : 'w-3/4')}
              aria-hidden="true"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface AppFormSkeletonProps {
  className?: string;
  fields?: number;
  columns?: 1 | 2 | 3;
}

export function AppFormSkeleton({ className, fields = 6, columns = 2 }: AppFormSkeletonProps) {
  return (
    <div aria-busy="true" role="status" className={className}>
      <span className="sr-only">Loading content</span>
      <FormSkeleton fields={fields} columns={columns} />
    </div>
  );
}

export interface AppButtonLoaderProps {
  className?: string;
  compact?: boolean;
}

export function AppButtonLoader({ className, compact = false }: AppButtonLoaderProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        LOADER_BLOCK,
        'inline-flex rounded-full',
        compact ? 'h-3 w-3' : 'h-3.5 w-3.5',
        className,
      )}
    />
  );
}

export interface AppInlineLoaderProps {
  className?: string;
  label?: string;
}

export function AppInlineLoader({ className, label = 'Loading content' }: AppInlineLoaderProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-muted-foreground text-[11px]', className)} aria-live="polite">
      <span className={cn(LOADER_BLOCK, 'inline-flex h-2.5 w-6 rounded-full')} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface AppEmptyStateProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

export function AppEmptyState({
  title = 'No data available',
  description = 'No records are available for this view.',
  action,
  className,
}: AppEmptyStateProps) {
  return (
    <div className={cn('p-8 text-center', className)}>
      <AppSkeleton className="h-6 w-6 mx-auto mb-3 rounded-full" aria-hidden="true" />
      <h3 className="text-[14px] font-medium text-foreground">{title}</h3>
      <p className="mt-1 text-[12px] text-muted-foreground">{description}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}
